import {Button, Image, message, Tag} from 'antd'
import {course_submitIdentityDocuments_Api} from 'apis'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useRef, useState} from 'react'
import {BiCloudUpload} from 'react-icons/bi'
import {MdOutlineBlock} from 'react-icons/md'
import {RiShieldCheckFill} from 'react-icons/ri'
import {formatDate} from '../../../../../helpers/datetimeHelpers'
import {HiOutlineShieldCheck} from "react-icons/hi2";
import classNames from "classnames";

export const IDENTITY_STATUS = {
    UNVERIFIED: 0,
    PENDING: -1,
    VERIFIED: 1,
    REJECTED: -2,
    BLOCKED: -3,
}

// description: câu mô tả ngắn theo từng trạng thái, hiện ở banner lớn (xem render bên dưới) - gộp
// chung vào đây thay vì rải t() key rời ở chỗ render, dễ theo dõi 5 trạng thái cùng 1 chỗ.
export const IDENTITY_STATUS_CONFIG = {
    [IDENTITY_STATUS.UNVERIFIED]: {label: 'identity unverified', description: 'identity unverified description', color: 'grey', banner: 'bg-slate-50 border-color-100', icon: 'bg-slate-300'},
    [IDENTITY_STATUS.PENDING]: {label: 'identity pending', description: 'identity pending description', color: 'orange', banner: 'bg-orange-50 border-orange-100', icon: 'bg-orange-400'},
    [IDENTITY_STATUS.VERIFIED]: {label: 'identity verified', description: 'identity verified description', color: 'green', banner: 'bg-green-50 border-green-100', icon: 'bg-green-500'},
    [IDENTITY_STATUS.REJECTED]: {label: 'identity rejected', description: 'identity rejected description', color: 'red', banner: 'bg-red-50 border-red-100', icon: 'bg-red-400'},
    [IDENTITY_STATUS.BLOCKED]: {label: 'identity blocked', description: 'identity blocked description', color: 'purple', banner: 'bg-purple-50 border-purple-100', icon: 'bg-purple-400'},
}

export const FileUploadArea = ({label, file, existingUrl, onChange}) => {
    const t = useTranslate()
    const inputRef = useRef(null)
    const previewUrl = file ? URL.createObjectURL(file) : existingUrl

    return (
        <div className="flex flex-col items-center gap-1.5">
            <span className="text-xs text-color-400">{label}</span>
            <button
                type="button"
                className="relative border border-dashed border-color-200 hover:border-indigo-400 background rounded-lg transition-colors overflow-hidden"
                style={{width: 120, height: 80}}
                onClick={() => inputRef.current && inputRef.current.click()}>
                {previewUrl ? (
                    <>
                        <img
                            src={previewUrl}
                            className="w-full h-full object-cover"
                            alt={label}
                        />
                        <div
                            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded">
                            <BiCloudUpload size={20} className="text-white"/>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-1 px-2">
                        <BiCloudUpload size={22} className="text-color-300"/>
                        <span
                            className="text-2xs text-color-400 text-center leading-tight">{t('click to upload')}</span>
                    </div>
                )}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files && e.target.files[0]
                    if (f) onChange(f)
                    e.target.value = ''
                }}
            />
        </div>
    )
}

export const IdentityVerificationSection = ({item, onRefresh}) => {
    const t = useTranslate()
    const [frontFile, setFrontFile] = useState(null)
    const [backFile, setBackFile] = useState(null)

    const status = item.identity_status != null ? item.identity_status : IDENTITY_STATUS.UNVERIFIED
    const cfg = IDENTITY_STATUS_CONFIG[status] ?? IDENTITY_STATUS_CONFIG[IDENTITY_STATUS.UNVERIFIED]

    const isVerified = status === IDENTITY_STATUS.VERIFIED
    const isPending = status === IDENTITY_STATUS.PENDING
    const isBlocked = status === IDENTITY_STATUS.BLOCKED
    const isRejected = status === IDENTITY_STATUS.REJECTED
    const canUpload = item.edit === true && !isVerified && !isBlocked

    const {handleAsyncAction: doSubmitDocuments, loading: submitting} = useAsyncAction({
        apiInfo: course_submitIdentityDocuments_Api,
        onSuccess: () => {
            message.success(t('documents submitted successfully'))
            setFrontFile(null)
            setBackFile(null)
            onRefresh && onRefresh()
        },
        onError: () => message.error(t('upload failed')),
    })

    const handleSubmit = useCallback(() => {
        const values = {}
        if (frontFile) values.front = frontFile
        if (backFile) values.back = backFile
        if (!values.front && !values.back) {
            message.warning(t('please upload at least one document'))
            return
        }
        doSubmitDocuments(values, {id: item.id})
    }, [frontFile, backFile, item.id, doSubmitDocuments, t])

    const hasDocs = item.identity_document_front_url || item.identity_document_back_url

    // Trước đây bọc trong Toggle (thu gọn/mở rộng) vì section này từng đứng CHUNG với các khối
    // thông tin khác trong 1 mặt - giờ section này LÀ nguyên nội dung của tab "Xác minh danh tính"
    // riêng (StudentViewCard.js) nên bỏ hẳn collapse, hiện đầy đủ luôn - chuyển tab đã là 1 lượt
    // thu gọn/mở rộng rồi, thêm Toggle nữa là thừa.
    return (
        <div className="flex flex-col gap-4">
            {/* Banner trạng thái - to, màu theo status (xem IDENTITY_STATUS_CONFIG), thay cho Tag nhỏ
                trước đây - khớp mockup mới. */}
            <div className={classNames("p-3.5 flex items-center justify-between gap-3 flex-wrap rounded-xl border", cfg.banner)}>
                <div className="flex items-center gap-3">
                    <div className={classNames("w-10 h-10 rounded-xl flex flex-center flex-shrink-0 text-white", cfg.icon)}>
                        <HiOutlineShieldCheck size={20}/>
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-sm text-gray-800">
                            {t('status')}{': '}{t(cfg.label)}
                        </span>
                        <span className="text-xs text-color-400 leading-snug">
                            {t(cfg.description)}
                        </span>
                        {isVerified && item.identity_verified_at && (
                            <span className="text-2xs text-gray-500 tracking-wider">
                                <span className="mr-1 text-color-400 italic">{t('at_time')}</span>
                                {formatDate(item.identity_verified_at, 'HH:mm - DD/MM/YYYY')}
                            </span>
                        )}
                    </div>
                </div>
                <Tag color={cfg.color} className="flex items-center gap-1 flex-shrink-0 w-fit">
                    {isVerified && <RiShieldCheckFill size={11} className="inline"/>}
                    {isBlocked && <MdOutlineBlock size={11} className="inline"/>}
                    {t(cfg.label)}
                </Tag>
            </div>

            {/* Rejection / blocked reason */}
            {(isRejected || isBlocked) && item.identity_rejected_reason && (
                <div className="flex gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600 italic">
                    {item.identity_rejected_reason}
                </div>
            )}

            {/* Pending message */}
            {isPending && (
                <div className="text-xs text-orange-500 italic">
                    {t('identity pending review')}
                </div>
            )}

            <div className="flex flex-col gap-2">
                <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                    {t('identity documents section title')}
                </span>
                {/* Ảnh đã nộp trước đó - hiện ĐỘC LẬP với canUpload (vd hồ sơ bị từ chối vẫn cần thấy
                    lại ảnh cũ đã nộp, đồng thời với 2 ô tải ảnh mới bên dưới). */}
                {hasDocs && (
                    <div className="flex gap-3 flex-wrap">
                        {item.identity_document_front_url && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-color-400">{t('identity document front')}</span>
                                <Image
                                    src={item.identity_document_front_url}
                                    width={100}
                                    height={68}
                                    className="rounded-lg object-cover border border-color-100"
                                    alt={t('identity document front')}
                                />
                            </div>
                        )}
                        {item.identity_document_back_url && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-color-400">{t('identity document back')}</span>
                                <Image
                                    src={item.identity_document_back_url}
                                    width={100}
                                    height={68}
                                    className="rounded-lg object-cover border border-color-100"
                                    alt={t('identity document back')}
                                />
                            </div>
                        )}
                    </div>
                )}
                {canUpload && (
                    <div className="flex flex-col gap-2 items-end">
                        <div className="flex flex-wrap gap-4 w-full">
                            <FileUploadArea
                                label={t('identity document front')}
                                file={frontFile}
                                existingUrl={null}
                                onChange={setFrontFile}
                            />
                            <FileUploadArea
                                label={t('identity document back')}
                                file={backFile}
                                existingUrl={null}
                                onChange={setBackFile}
                            />
                        </div>
                        <Button
                            size="small"
                            color="primary" variant="solid"
                            loading={submitting}
                            disabled={!frontFile && !backFile}
                            onClick={handleSubmit}
                            className="rounded-md no-border text-xs">
                            {isRejected ? t('re-upload identity documents') : t('submit documents')}
                        </Button>
                    </div>
                )}
                {!hasDocs && !canUpload && (
                    <span className="text-xs text-color-400 italic">{t('no documents uploaded')}</span>
                )}
            </div>
        </div>
    )
}
